// Fake data for the brand portal's Reports page.
//
// Built from the same ~240-screen fleet as the My Campaigns demo, so a creative
// that shows as running on 30 screens there reports plays on those 30 screens
// here. Every screen x creative x day gets a deterministic play count, so a
// reload, a different window or a creative filter all stay consistent.
//
// Switched on exactly like the campaigns demo (`?demo=1` on `npm start`, or
// REACT_APP_BRAND_DEMO_DATA=true). PDF, Excel and email are generated by the
// server from real play logs, so they do NOT show this data.

import { demoScreens } from "./brandCampaignDemo";
import { toDisplayDateInput } from "../utils/dateUtils";
import type {
  BrandReport,
  BrandReportCreativeOption,
  BrandReportCreativeRow,
  BrandReportFilter,
  BrandReportLocationRow,
  BrandReportScreenRow,
  BrandReportScreensPage,
} from "../schemas/brandReportSchema";

export { isBrandDemoData } from "./brandCampaignDemo";

const DAY_MS = 86_400_000;
const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

/** Show the file name a brand uploaded, not the demo fleet's internal id. */
const fileName = (name: string) => name.replace(/^demo-\d+-[0-9a-f]+-/, "");

/** Stand-in ids, so the city and area filters work on the fake fleet. */
const cityIdOf = (city?: string | null) => `demo-city:${city ?? ""}`;
const areaIdOf = (city?: string | null, area?: string | null) => `demo-area:${city ?? ""}|${area ?? ""}`;

const shiftDay = (key: string, days: number) =>
  new Date(Date.parse(`${key}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

/** ISO weekday index, 0 = Monday. */
const weekdayOf = (key: string) => (new Date(`${key}T00:00:00Z`).getUTCDay() + 6) % 7;

/** FNV-1a, scaled to [0, 1): a stable "random" number per key. */
const noise = (key: string) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 4294967296;
};

/** How a slot's plays spread over its hours — lunch and dinner are busiest. */
const hourWeights = (startTime: string, endTime: string): number[] => {
  const from = Number(startTime.slice(0, 2));
  const to = Number(endTime.slice(0, 2));
  const weights = Array.from({ length: 24 }, (_, h): number => {
    if (h < from || h > to) return 0;
    if (h >= 12 && h <= 14) return 2;
    if (h >= 19 && h <= 21) return 2.5;
    if (h < 7) return 0.3;
    return 1;
  });
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  return weights.map((w) => w / total);
};

interface Totals {
  plays: number;
  airtime: number;
}

const build = (filter: BrandReportFilter) => {
  const today = toDisplayDateInput(new Date());
  const lastDay = filter.endDate < today ? filter.endDate : today;
  const wanted = new Set(filter.creativeIds ?? []);

  const summary: Totals = { plays: 0, airtime: 0 };
  const daily = new Map<string, Totals & { screens: Set<string> }>();
  const hourly = Array.from({ length: 7 }, () => new Array<number>(24).fill(0));
  const creatives = new Map<
    string,
    Omit<BrandReportCreativeRow, "screens" | "lastPlayedAt"> & { screens: Set<string>; last: string }
  >();
  const locations = new Map<
    string,
    Totals & {
      cityId: string;
      city: string;
      regionId: string;
      region: string;
      screens: Set<string>;
      restaurants: Set<string>;
    }
  >();
  const screens = new Map<string, BrandReportScreenRow & { ids: Set<string>; last: string }>();

  for (const screen of demoScreens()) {
    if (filter.cityId && cityIdOf(screen.city) !== filter.cityId) continue;
    if (filter.regionId && areaIdOf(screen.city, screen.region) !== filter.regionId) continue;
    for (const campaign of screen.campaigns) {
      if (campaign.status === "UPCOMING") continue;
      if (wanted.size && !wanted.has(campaign.creativeId)) continue;
      // The demo fleet always fills these; the real type allows gaps.
      const raw = campaign.slots[0];
      if (!raw?.startDate || !raw.endDate) continue;
      const slot = {
        startDate: raw.startDate,
        endDate: raw.endDate,
        startTime: raw.startTime ?? "00:00",
        endTime: raw.endTime ?? "23:59",
        daysOfWeek: raw.daysOfWeek ?? WEEKDAYS,
      };
      const from = filter.startDate > slot.startDate ? filter.startDate : slot.startDate;
      const to = lastDay < slot.endDate ? lastDay : slot.endDate;
      if (from > to) continue;

      const clip = campaign.plays ? Math.max(5, Math.round(campaign.playDuration / campaign.plays)) : 15;
      const hours = Math.max(1, Number(slot.endTime.slice(0, 2)) - Number(slot.startTime.slice(0, 2)) + 1);
      const spread = hourWeights(slot.startTime, slot.endTime);
      const days = new Set(slot.daysOfWeek);

      for (let day = from; day <= to; day = shiftDay(day, 1)) {
        const wd = weekdayOf(day);
        if (!days.has(WEEKDAYS[wd])) continue;
        const n = noise(`${screen.screenId}|${campaign.creativeId}|${day}`);
        // The odd day a screen was switched off and played nothing.
        if (n < 0.07) continue;
        const weekend = wd >= 5 ? 1.25 : 1;
        const plays = Math.round(hours * (1.2 + n * 2.4) * weekend);
        const airtime = plays * clip;

        summary.plays += plays;
        summary.airtime += airtime;

        const d = daily.get(day) ?? { plays: 0, airtime: 0, screens: new Set<string>() };
        d.plays += plays;
        d.airtime += airtime;
        d.screens.add(screen.screenId);
        daily.set(day, d);

        spread.forEach((w, h) => {
          hourly[wd][h] += plays * w;
        });

        const c =
          creatives.get(campaign.creativeId) ??
          {
            creativeId: campaign.creativeId,
            name: fileName(campaign.name),
            thumbnailUrl: campaign.thumbnailUrl ?? null,
            fileType: campaign.fileType ?? null,
            startDate: slot.startDate,
            endDate: slot.endDate,
            plays: 0,
            airtime: 0,
            screens: new Set<string>(),
            last: day,
          };
        c.plays += plays;
        c.airtime += airtime;
        c.screens.add(screen.screenId);
        if (day > c.last) c.last = day;
        creatives.set(campaign.creativeId, c);

        const locKey = `${screen.city}|${screen.region}`;
        const loc =
          locations.get(locKey) ??
          {
            cityId: cityIdOf(screen.city),
            city: screen.city ?? "",
            regionId: areaIdOf(screen.city, screen.region),
            region: screen.region ?? "",
            plays: 0,
            airtime: 0,
            screens: new Set<string>(),
            restaurants: new Set<string>(),
          };
        loc.plays += plays;
        loc.airtime += airtime;
        loc.screens.add(screen.screenId);
        loc.restaurants.add(screen.restaurantName ?? screen.screenId);
        locations.set(locKey, loc);

        const s =
          screens.get(screen.screenId) ??
          {
            screenId: screen.screenId,
            screenName: screen.screenName,
            restaurantName: screen.restaurantName ?? null,
            city: screen.city ?? null,
            region: screen.region ?? null,
            plays: 0,
            airtime: 0,
            creatives: 0,
            lastPlayedAt: null,
            ids: new Set<string>(),
            last: day,
          };
        s.plays += plays;
        s.airtime += airtime;
        s.ids.add(campaign.creativeId);
        if (day > s.last) s.last = day;
        screens.set(screen.screenId, s);
      }
    }
  }

  // A play late on the last day, never in the future.
  const lastPlayed = (day: string, key: string) =>
    new Date(
      Math.min(Date.now() - 60_000, Date.parse(`${day}T21:30:00+05:30`)) -
        Math.floor(noise(key) * 5 * 3600_000),
    ).toISOString();

  return { summary, daily, hourly, creatives, locations, screens, lastPlayed, lastDay };
};

// The last few windows asked for; the page re-reads the same one a lot.
const memo = new Map<string, ReturnType<typeof build>>();
const cachedBuild = (filter: BrandReportFilter) => {
  const key = JSON.stringify([
    filter.startDate,
    filter.endDate,
    [...(filter.creativeIds ?? [])].sort(),
    filter.cityId ?? "",
    filter.regionId ?? "",
  ]);
  let hit = memo.get(key);
  if (!hit) {
    hit = build(filter);
    if (memo.size > 8) memo.delete(memo.keys().next().value as string);
    memo.set(key, hit);
  }
  return hit;
};

export const demoBrandReport = (filter: BrandReportFilter): BrandReport => {
  const { summary, daily, hourly, creatives, locations, screens, lastPlayed, lastDay } =
    cachedBuild(filter);

  const days = [];
  for (let day = filter.startDate; day <= lastDay; day = shiftDay(day, 1)) {
    const d = daily.get(day);
    days.push({ date: day, plays: d?.plays ?? 0, airtime: d?.airtime ?? 0, screens: d?.screens.size ?? 0 });
  }
  const screenDays = days.reduce((sum, d) => sum + d.screens, 0);

  const byLocation: BrandReportLocationRow[] = [...locations.values()]
    .map(({ screens: s, restaurants, ...rest }) => ({ ...rest, screens: s.size, restaurants: restaurants.size }))
    .sort((a, b) => b.plays - a.plays);

  return {
    brandName: "Demo Brand",
    startDate: filter.startDate,
    endDate: filter.endDate,
    summary: {
      plays: summary.plays,
      airtime: summary.airtime,
      screens: screens.size,
      restaurants: new Set([...screens.values()].map((s) => s.restaurantName)).size,
      cities: new Set(byLocation.map((l) => l.city)).size,
      creatives: creatives.size,
      activeDays: days.filter((d) => d.plays > 0).length,
      avgPlaysPerScreenDay: screenDays ? Math.round((summary.plays / screenDays) * 100) / 100 : 0,
    },
    daily: days,
    hourly: hourly.flatMap((row, wd) =>
      row.map((plays, hour) => ({ dayOfWeek: wd + 1, hour, plays: Math.round(plays) })),
    ),
    byCreative: [...creatives.values()]
      .map(({ screens: s, last, ...rest }) => ({
        ...rest,
        screens: s.size,
        lastPlayedAt: lastPlayed(last, rest.creativeId),
      }))
      .sort((a, b) => b.plays - a.plays),
    byLocation,
  };
};

export const demoBrandReportScreens = (
  filter: BrandReportFilter,
  params: { search?: string; limit?: number; offset?: number },
): BrandReportScreensPage => {
  const { screens, lastPlayed } = cachedBuild(filter);
  const q = (params.search ?? "").trim().toLowerCase();
  const rows = [...screens.values()]
    .filter(
      (s) =>
        !q ||
        [s.screenName, s.restaurantName, s.city, s.region].some((v) => v?.toLowerCase().includes(q)),
    )
    .sort((a, b) => b.plays - a.plays || a.screenName.localeCompare(b.screenName));
  const offset = params.offset ?? 0;
  const limit = params.limit ?? 25;
  return {
    totalCount: rows.length,
    items: rows.slice(offset, offset + limit).map(({ ids, last, ...rest }) => ({
      ...rest,
      creatives: ids.size,
      lastPlayedAt: lastPlayed(last, rest.screenId),
    })),
  };
};

export const demoBrandReportCreatives = (): BrandReportCreativeOption[] => {
  const seen = new Map<string, BrandReportCreativeOption>();
  for (const screen of demoScreens()) {
    for (const c of screen.campaigns) {
      if (!seen.has(c.creativeId)) {
        seen.set(c.creativeId, {
          id: c.creativeId,
          name: fileName(c.name),
          thumbnailUrl: c.thumbnailUrl ?? null,
          isDeleted: false,
        });
      }
    }
  }
  const options = [...seen.values()];
  // One removed creative, so the picker's "Removed" badge shows.
  const expired = demoScreens()
    .flatMap((s) => s.campaigns)
    .find((c) => c.status === "EXPIRED");
  const removed = options.find((o) => o.id === expired?.creativeId);
  if (removed) removed.isDeleted = true;
  return options;
};
